import { Effect, Option, Result, Schema } from "effect";
import { Clock } from "../../clock/service.ts";
import { AuthorizationError, DEFAULT_PLATFORM_ASSERTION_HEADER, StableIdentityText,
  type LocalOwnerResolverConfig, type PlatformAssertionValidationConfig } from "../model.ts";
import { PlatformAssertionIo, authorizationIdentityError, type AuthorizationIdentityError } from "./io.ts";

const validatedPlatformIdentity = Symbol("validated-platform-identity");
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder("utf-8", { fatal: true });
const PlatformIdentityValue = /* @__PURE__ */ (() => Schema.Struct({
  [validatedPlatformIdentity]: Schema.Literal(true),
  issuer: Schema.String,
  subject: Schema.String,
  groups: Schema.Array(Schema.String),
  authenticatedAt: Schema.Number,
  expiresAt: Schema.Number,
  source: Schema.Literals(["signed-platform-assertion", "local-owner-resolver"]),
  policySource: Schema.String
}))();
type PlatformIdentityValue = typeof PlatformIdentityValue.Type;
const isPlatformIdentityValue = /* @__PURE__ */ (() => Schema.is(PlatformIdentityValue))();
// This is an opaque in-process capability. Exposing the private symbol through
// public Struct.fields would let a caller construct an authenticated identity.
export const ValidatedPlatformIdentity = /* @__PURE__ */ (() => Schema.declare<PlatformIdentityValue>(
  (value): value is PlatformIdentityValue =>
    typeof value === "object" && value !== null &&
    Reflect.get(value, validatedPlatformIdentity) === true && isPlatformIdentityValue(value),
  { identifier: "ValidatedPlatformIdentity" }
))();
export type ValidatedPlatformIdentity = typeof ValidatedPlatformIdentity.Type;

export const PlatformIdentityCapability = /* @__PURE__ */ (() => Schema.Struct({
  kind: Schema.Literal("platform-identity"),
  identity: ValidatedPlatformIdentity,
}))();
export type PlatformIdentityCapability = typeof PlatformIdentityCapability.Type;

export const BootstrapCapability = /* @__PURE__ */ (() => Schema.Struct({
  kind: Schema.Literal("station-bootstrap"),
  identity: ValidatedPlatformIdentity,
  policySource: Schema.String,
}))();
export type BootstrapCapability = typeof BootstrapCapability.Type;

const malformed = () => new AuthorizationError("invalid-assertion", "The platform assertion is malformed.");
const read = <A>(body: () => A) => Effect.try({ try: body, catch: authorizationIdentityError });
const inspect = <A>(body: () => A) => Result.try({ try: body, catch: authorizationIdentityError });
const decodeResult = <A>(schema: Schema.ConstraintDecoder<A>, value: unknown, refusal: () => AuthorizationError) =>
  inspect(() => Schema.decodeUnknownResult(schema)(value)).pipe(
    Result.flatMap(result => Result.mapError(result, refusal)));
const decode = <A>(schema: Schema.ConstraintDecoder<A>, value: unknown, refusal: () => AuthorizationError) =>
  read(() => Schema.decodeUnknownResult(schema)(value)).pipe(
    Effect.flatMap(result => Effect.fromResult(Result.mapError(result, refusal))));
const SegmentText = Schema.String.check(Schema.makeFilter(value => /^[A-Za-z0-9_-]*$/.test(value) && value.length % 4 !== 1));
const segment = (value: string) => Schema.decodeUnknownResult(SegmentText)(value).pipe(
  Result.flatMap(Schema.decodeUnknownResult(Schema.Uint8ArrayFromBase64Url)),
  Result.map(bytes => Uint8Array.from(bytes)), Result.mapError(malformed));
const JsonObject = Schema.fromJsonString(Schema.Record(Schema.String, Schema.Unknown));
const parseObject = (value: string) => Effect.try({ try: () => Schema.decodeUnknownResult(JsonObject)(textDecoder.decode(
  Result.getOrThrowWith(segment(value), error => error))), catch: malformed }).pipe(
  Effect.flatMap(result => Effect.fromResult(Result.mapError(result, malformed))));
const Segments = Schema.Tuple([Schema.String.check(Schema.isMinLength(1)), Schema.String.check(Schema.isMinLength(1)), Schema.String.check(Schema.isMinLength(1))]);
const KeyMetadata = Schema.Struct({ type: Schema.Literal("public"),
  usages: Schema.Array(Schema.String).check(Schema.makeFilter(value => value.includes("verify"))),
  algorithm: Schema.Struct({ name: Schema.Literal("RSASSA-PKCS1-v1_5"), hash: Schema.Struct({ name: Schema.Literal("SHA-256") }) }) });
const Times = Schema.Struct({ issuedAt: Schema.Int, notBefore: Schema.Int, expiresAt: Schema.Int });
const stable = (value: unknown, maximum = 256) => Option.getOrNull(Schema.decodeUnknownOption(StableIdentityText(maximum))(value));

/** Pure compatibility entry: the caller owns the clock, and the private symbol stays here. */
export const resolveLocalOwnerIdentityAt = (config: LocalOwnerResolverConfig, now: number): Result.Result<ValidatedPlatformIdentity, AuthorizationError | AuthorizationIdentityError> =>
  Result.try({ try: () => {
    if (config.deploymentEnvironment === "production") return Result.fail(new AuthorizationError("local-resolver-forbidden", "The local owner resolver is forbidden in production."));
    const subject = stable(config.subject), policySource = stable(config.policySource, 512), groups = config.groups ?? [];
    if (!subject || !policySource || groups.some(group => stable(group) === null)) return Result.fail(new AuthorizationError("bootstrap-denied", "The local owner policy is invalid."));
    return Result.succeed(Object.freeze({ [validatedPlatformIdentity]: true as const, issuer: "workerstation:local-owner", subject,
      groups: Object.freeze([...groups]), authenticatedAt: now, expiresAt: now + 5 * 60_000, source: "local-owner-resolver" as const, policySource }));
  }, catch: authorizationIdentityError }).pipe(Result.flatMap(result => result));

export const resolveLocalOwnerIdentity = Effect.fn("Authorization.resolveLocalOwnerIdentity")(function* (config: LocalOwnerResolverConfig, now?: number) {
  const instant = now === undefined ? yield* (yield* Clock).milliseconds : now;
  return yield* Effect.fromResult(resolveLocalOwnerIdentityAt(config, instant));
});

/** Accepted key resolution and verification settle in the caller's event. */
export const validateSignedPlatformAssertionAt = Effect.fn("Authorization.validateSignedPlatformAssertionAt")(function* (
  assertion: string, config: PlatformAssertionValidationConfig, now: number
) {
  const io = yield* PlatformAssertionIo;
  const maxBytes = yield* read(() => config.maxAssertionBytes ?? 16_384);
  const token = yield* decode(Schema.String.check(Schema.makeFilter(value => value.length > 0 && !(textEncoder.encode(value).byteLength > maxBytes))), assertion, malformed);
  const [encodedHeader, encodedPayload, encodedSignature] = yield* decode(Segments, token.split("."), malformed);
  const header = yield* parseObject(encodedHeader), payload = yield* parseObject(encodedPayload);
  const allowed = yield* read(() => config.allowedAlgorithms ?? ["RS256"]);
  const algorithm = yield* decode(Schema.Literal("RS256").check(Schema.makeFilter(value => allowed.includes(value))), header.alg,
    () => new AuthorizationError("invalid-assertion-signature", "The platform assertion uses an untrusted signature algorithm."));
  yield* decode(Schema.UndefinedOr(Schema.Literal("JWT")), header.typ,
    () => new AuthorizationError("invalid-assertion", "The platform assertion type is invalid."));
  const keyId = yield* decode(Schema.UndefinedOr(StableIdentityText(512)), header.kid,
    () => new AuthorizationError("invalid-assertion", "The platform assertion key id is invalid."));
  const key = yield* io.resolveKey(config, { algorithm, ...(keyId === undefined ? {} : { keyId }) }).pipe(Effect.catch(() => Effect.succeed(null)));
  if (!key) return yield* new AuthorizationError("invalid-assertion-signature", "The platform assertion signing key is not trusted.");
  const metadata = yield* read(() => {
    const algorithm = key.algorithm as RsaHashedKeyAlgorithm;
    return { type: key.type, usages: key.usages, algorithm: { name: algorithm.name, hash: { name: algorithm.hash?.name } } };
  });
  yield* decode(KeyMetadata, metadata, () => new AuthorizationError("invalid-assertion-signature", "The platform assertion signing key is not an RS256 verification key."));
  const verified = yield* io.verify(key, () => Result.getOrThrowWith(segment(encodedSignature), error => error),
    () => textEncoder.encode(`${encodedHeader}.${encodedPayload}`)).pipe(Effect.catch(() => Effect.succeed(false)));
  if (!verified) return yield* new AuthorizationError("invalid-assertion-signature", "The platform assertion signature is invalid.");
  // Preserve the native synchronous continuation: a queued policy rotation
  // must not run between claim validation and capability construction.
  return yield* Effect.fromResult(Result.gen(function* () {
    yield* decodeResult(Schema.Literal(yield* inspect(() => config.issuer)), payload.iss,
      () => new AuthorizationError("invalid-assertion-issuer", "The platform assertion issuer is invalid."));
    const audience = yield* inspect(() => config.audience);
    yield* decodeResult(Schema.Union([Schema.Literal(audience), Schema.Array(Schema.String).check(Schema.makeFilter(value => value.includes(audience)))]), payload.aud,
      () => new AuthorizationError("invalid-assertion-audience", "The platform assertion audience is invalid."));
    const subject = yield* decodeResult(StableIdentityText(), payload.sub,
      () => new AuthorizationError("invalid-assertion-subject", "The platform assertion has no stable subject."));
    const nowSeconds = Math.floor(now / 1000), skew = yield* inspect(() => Math.max(0, config.clockSkewSeconds ?? 60));
    const maxLifetime = yield* inspect(() => Math.max(1, config.maxTokenLifetimeSeconds ?? 600));
    const { issuedAt, expiresAt } = yield* decodeResult(Times.check(Schema.makeFilter(value => !(
      value.expiresAt <= value.issuedAt || value.expiresAt - value.issuedAt > maxLifetime || value.issuedAt > nowSeconds + skew ||
      value.notBefore > nowSeconds + skew || value.expiresAt < nowSeconds - skew
    ))), { issuedAt: payload.iat, notBefore: payload.nbf === undefined ? payload.iat : payload.nbf, expiresAt: payload.exp },
    () => new AuthorizationError("invalid-assertion-time", "The platform assertion is outside its valid time window."));
    const groups = (yield* decodeResult(Schema.UndefinedOr(Schema.Array(StableIdentityText())), payload[yield* inspect(() => config.groupsClaim ?? "groups")],
      () => new AuthorizationError("invalid-assertion", "The platform assertion groups are invalid."))) ?? [];
    return yield* inspect(() => Object.freeze({ [validatedPlatformIdentity]: true as const, issuer: config.issuer, subject,
      groups: Object.freeze([...groups]), authenticatedAt: issuedAt * 1000, expiresAt: expiresAt * 1000,
      source: "signed-platform-assertion" as const, policySource: `signed:${config.issuer}` }));
  }));
}, Effect.uninterruptible);

export const validateSignedPlatformAssertion = Effect.fn("Authorization.validateSignedPlatformAssertion")(function* (
  assertion: string, config: PlatformAssertionValidationConfig, now?: number
) {
  const instant = now === undefined ? yield* (yield* Clock).milliseconds : now;
  return yield* validateSignedPlatformAssertionAt(assertion, config, instant);
});

/** Forwarded identity headers never supply authority. */
export const authenticatePlatformRequest = Effect.fn("Authorization.authenticatePlatformRequest")(function* (
  request: Request, config: PlatformAssertionValidationConfig & { assertionHeader?: string }, now?: number
) {
  const instant = now === undefined ? yield* (yield* Clock).milliseconds : now;
  const assertion = yield* read(() => request.headers.get(config.assertionHeader ?? DEFAULT_PLATFORM_ASSERTION_HEADER));
  if (!assertion) return yield* new AuthorizationError("authentication-required", "A signed platform identity assertion is required.");
  return yield* validateSignedPlatformAssertionAt(assertion, config, instant);
});
